import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import { createContext, useContext } from 'use-context-selector';

import { ChooseGroupPage, NotFoundGroupPage } from '~components/pages';
import { useLoggedInUserInfoContext } from '~providers';
import { LoggedInUserInfo } from '~types';

export type GroupInfoType = LoggedInUserInfo['groups'][0];

type GroupInfoContextType = {
  groupId: string | null;
  groupInfo: GroupInfoType | null;
  selectGroup: (id: string) => void;
};

const GroupInfoContext = createContext<GroupInfoContextType>({
  groupId: null,
  groupInfo: null,
  selectGroup: () => null,
});

type Props = {
  children: React.ReactNode;
};

export const SELECTED_GROUP_ID = 'cf-group-id';

export const GroupInfoProvider: React.FC<Props> = ({ children }) => {
  const { loggedInUserInfo } = useLoggedInUserInfoContext();
  const [groupId, setGroupId] = useState<string | null>(null);
  const [groupInfo, setGroupInfo] = useState<GroupInfoType | null>(null);
  const [loading, setLoading] = useState(true);
  const onUpdateGroupId = (id: string | null) => {
    setGroupId(id);
    if (id) {
      localStorage.setItem(SELECTED_GROUP_ID, id);
    }
  };

  useEffect(() => {
    if (loggedInUserInfo?.groups) {
      const selectedGroupId = localStorage.getItem(SELECTED_GROUP_ID);
      const idx =
        (loggedInUserInfo?.groups ?? []).length === 1
          ? 0
          : loggedInUserInfo?.groups.findIndex(
              (item) => item.id === selectedGroupId,
            );

      if (loggedInUserInfo?.groups.length <= 1 || idx !== -1) {
        const groupDetail = idx !== -1 ? loggedInUserInfo?.groups[idx] : null;
        onUpdateGroupId(groupDetail ? groupDetail.id : null);
      }
      setLoading(false);
    }
  }, [loggedInUserInfo]);

  useEffect(() => {
    if (groupId && loggedInUserInfo?.groups) {
      const idx = loggedInUserInfo?.groups.findIndex(
        (item) => item.id === groupId,
      );
      const groupDetail = idx !== -1 ? loggedInUserInfo?.groups[idx] : null;
      setGroupInfo(groupDetail);
    }
  }, [groupId, loggedInUserInfo?.groups]);

  const renderChildren = () => {
    if (groupId) {
      return children;
    }
    const userGroupsLength = get(loggedInUserInfo, 'groups.length', 0);
    const isBelongAdminOrSupperAdminOrManufacturer = [
      'ADMIN',
      'SUPER_ADMIN',
      'MANUFACTURER',
    ].includes(loggedInUserInfo?.role ?? '');
    if (userGroupsLength == 0 && !isBelongAdminOrSupperAdminOrManufacturer) {
      return <NotFoundGroupPage />;
    }
    if (userGroupsLength > 1) {
      return <ChooseGroupPage selectGroup={onUpdateGroupId} />;
    }
    return children;
  };

  return (
    <GroupInfoContext.Provider
      value={{
        groupId,
        groupInfo,
        selectGroup: onUpdateGroupId,
      }}
    >
      {!loading && renderChildren()}
    </GroupInfoContext.Provider>
  );
};

export const useGroupInfoContext = () => useContext(GroupInfoContext);
